/**
 * ShareDialog: create, display, rotate, and revoke a plan's public share link.
 *
 * The link opens a read-only view of the plan (map + statistics) where
 * anyone can co-sign it or fork their own editable copy. Revoking makes
 * previously shared URLs stop resolving.
 */
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { getShareInfo, revokeShare, sharePlan } from '../../lib/sharing';

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return `${error.code}: ${error.message}`;
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}

export function ShareDialog({
  planId,
  planName,
  onClose,
}: {
  planId: string;
  planName: string;
  onClose: () => void;
}): JSX.Element {
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Escape closes the dialog (keyboard users have no other way out).
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getShareInfo(planId)
      .then((info) => {
        if (!cancelled) setShareUrl(info.share_url);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [planId]);

  async function handleCreateOrRotate(): Promise<void> {
    setWorking(true);
    setError(null);
    try {
      const result = await sharePlan(planId);
      setShareUrl(result.share_url);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setWorking(false);
    }
  }

  async function handleRevoke(): Promise<void> {
    setWorking(true);
    setError(null);
    try {
      await revokeShare(planId);
      setShareUrl(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setWorking(false);
    }
  }

  async function handleCopy(): Promise<void> {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Clipboard API unavailable (permissions, insecure context): fall back
      // to selecting the input so the user can copy manually.
      const input = document.getElementById('share-link-input');
      if (input instanceof HTMLInputElement) input.select();
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 backdrop-blur-[2px] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="share-title" className="text-base font-semibold text-ink">
          Share &ldquo;{planName}&rdquo;
        </h2>

        {loading ? (
          <p className="mt-4 text-sm text-stone-500">Loading share status…</p>
        ) : shareUrl ? (
          <div className="mt-4 space-y-3">
            <label className="block text-sm text-stone-700">
              Anyone with this link can view your plan
              <div className="mt-1 flex gap-2">
                <input
                  id="share-link-input"
                  type="text"
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  className="min-w-0 flex-1 rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 font-mono text-xs text-stone-800"
                />
                <button
                  type="button"
                  onClick={() => void handleCopy()}
                  className="shrink-0 rounded-lg bg-tenant-primary px-3 py-2 text-sm font-medium text-white hover:bg-tenant-strong"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void handleCreateOrRotate()}
                disabled={working}
                title="Generate a new link; the old one stops working"
                className="rounded-lg border border-stone-300 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              >
                {working ? 'Working…' : '↻ Rotate link'}
              </button>
              <button
                type="button"
                onClick={() => void handleRevoke()}
                disabled={working}
                title="The current link stops working immediately"
                className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {working ? 'Working…' : 'Revoke link'}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-stone-600">
              Create a public link so friends and neighbors can view your plan, co-sign
              it, or make their own copy to edit.
            </p>
            <button
              type="button"
              onClick={() => void handleCreateOrRotate()}
              disabled={working}
              className="mt-3 rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong disabled:opacity-50"
            >
              {working ? 'Creating…' : 'Create share link'}
            </button>
          </div>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}

        <p className="mt-4 rounded-lg bg-stone-50 px-3 py-2 text-xs leading-relaxed text-stone-600">
          Anyone with this link can <strong>view</strong> your plan — no login needed.
          Viewers can co-sign it or fork an editable copy into their own account;
          they can never change your original. Rotating or revoking the link
          immediately disables previously shared URLs.
        </p>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
